import React, { useState, useEffect } from 'react'
import {
  Typography, Spin, Table, Card, Button, message, Row, Col, Input
} from 'antd'
import IncidentModal from './IncidentModal'
import { incidentsService } from '../services/incidentsService';
import { formatBrowser } from '../utils/dateUtils';
import { useSession } from '../context/SessionContext'
import useIncidents from '../hooks/useIncidents';
import useTableSearchPagination from '../hooks/useTableSearchPagination';
import { useGlobalLoading } from '../context/LoadingContext';

const { Title, Paragraph } = Typography

export default function IncidentsView() {
  const { incidents, loading, fetchIncidents } = useIncidents();
  const { hasRole } = useSession()

  const [modalOpen, setModalOpen] = useState(false)
  const [mode, setMode] = useState('create')
  const [selectedIncident, setSelectedIncident] = useState(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(false)
  const [resolving, setResolving] = useState(false)
  const { startLoading, stopLoading } = useGlobalLoading();

  useEffect(() => {
    fetchIncidents()
  }, [])

  const columns = [
    { title: 'Incident Number', dataIndex: 'id', key: 'id' },
    { title: 'Plant', dataIndex: 'community', key: 'community' },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <span>({record.latitude}, {record.longitude})</span>
      )
    },
    {
      title: 'Timestamp',
      dataIndex: 'date',
      key: 'date',
      render: value => value ? formatBrowser(value, { seconds: true }) : '-'
    },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      key: 'createdBy'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status =>
        status === 1
          ? <span style={{ color: '#52c41a' }}>Resolved</span>
          : <span style={{ color: '#faad14' }}>Pending</span>
    },
    {
      title: 'Resolved by',
      dataIndex: 'resolvedBy',
      key: 'resolvedBy',
      render: v => v || '-'
    },
    {
      title: 'Resolved at',
      dataIndex: 'resolvedAt',
      key: 'resolvedAt',
      render: v => v ? formatBrowser(v, { seconds: true }) : '-'
    }
  ]

  const filterIncident = (i, searchText) => {
    const q = searchText.toLowerCase();
    const statusText = i.status === 1 ? 'resolved' : 'pending';

    return (
      String(i.id).includes(q) ||
      (i.community || '').toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q) ||
      statusText.includes(q) ||
      (i.date ? formatBrowser(i.date, { seconds: true }).toLowerCase().includes(q) : false) ||
      (i.createdBy || '').toLowerCase().includes(q) ||
      (i.resolvedBy || '').toLowerCase().includes(q) ||
      (i.resolvedAt ? formatBrowser(i.resolvedAt, { seconds: true }).toLowerCase().includes(q) : false)
    );
  };

  const {
    searchText,
    onSearchChange,
    filteredData: filteredIncidents,
    pagination
  } = useTableSearchPagination({
    data: incidents,
    filterFn: filterIncident
  });

  // Crear
  const openCreateModal = () => {
    setMode('create')
    setSelectedIncident(null)
    setModalOpen(true)
  }

  // Editar
  const openEditModal = record => {
    setMode('edit')
    setSelectedIncident(record)
    setModalOpen(true)
  }

  const handleCreate = async (values, form) => {
    try {
      startLoading();
      setCreating(true)
      await incidentsService.create(values);
      message.success('Incident created successfully')
      setModalOpen(false)
      form.resetFields()
      fetchIncidents()
    } catch {
      message.error('Failed to create Incident')
    } finally {
      setCreating(false)
      stopLoading();
    }
  }

  const handleUpdate = async (values) => {
    try {
      startLoading();
      setEditing(true)
      await incidentsService.update(selectedIncident.id, values);
      message.success('Incident updated successfully')
      setModalOpen(false)
      fetchIncidents()
    } catch {
      message.error('Failed to update Incident')
    } finally {
      setEditing(false)
      stopLoading();
    }
  }

  const handleResolve = async (id, form) => {
    startLoading();
    setResolving(true)
    try {
      await incidentsService.resolve(id, form.getFieldValue('observations'));
      message.success('Incident marked as resolved')
      setModalOpen(false)
      fetchIncidents()
    } catch {
      message.error('Failed to mark as resolved')
    } finally {
      setResolving(false)
      stopLoading();
    }
  }

  const handleReopen = async (id) => {
    try {
      startLoading();
      await incidentsService.reopen(id);
      message.success('Incident reopened')
      setModalOpen(false)
      fetchIncidents()
    } catch {
      message.error('Failed to reopen Incident')
    } finally {
      stopLoading();
    }
  }

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Incidents</Title>
        </Col>
      </Row>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Input
            placeholder="Search incidents..."
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
            allowClear
            style={{ maxWidth: 300 }}
          />
        </Col>
        <Col>
          {!hasRole('INCIDENTS_VIEW') && (
            <Button type="primary" onClick={openCreateModal}>
              Create Incident
            </Button>
          )}
        </Col>
      </Row>

      { !filteredIncidents.length ? (
        <Paragraph style={{ marginTop: 40 }}>
          No incidents found.
        </Paragraph>
      ) : (
        <Card style={{ marginTop: 12 }}>
          <Table
            rowKey="id"
            dataSource={filteredIncidents}
            columns={columns}
            pagination={pagination}
            size="small"
            onRow={record => ({
              onClick: () => openEditModal(record),
              style: { cursor: 'pointer' }
            })}
          />
        </Card>
      )}

      <IncidentModal
        open={modalOpen}
        mode={mode}
        initialValues={selectedIncident}
        status={selectedIncident?.status || 0}
        creating={creating}
        editing={editing}
        resolving={resolving}
        onCancel={() => setModalOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onResolve={handleResolve}
        onReopen={handleReopen}
        readOnly={hasRole('INCIDENTS_VIEW')}
      />
    </div>
  )
}
